import CheckIcon from '@mui/icons-material/Check'
import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import EditIcon from '@mui/icons-material/Edit'
import FilterListIcon from '@mui/icons-material/FilterList'
import PreviewIcon from '@mui/icons-material/Preview'
import RecyclingIcon from '@mui/icons-material/Recycling'
import RestoreIcon from '@mui/icons-material/Restore'
import UnarchiveIcon from '@mui/icons-material/Unarchive'
import { ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material'
import { Box } from '@mui/system'
import { DataGrid, GridCellParams } from '@mui/x-data-grid'
import { t } from 'i18next'
import { useCallback, useContext, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ActivityContext } from 'src/context/ActivityContext'
import { getPermissions } from 'src/service/authService'
import { Dropdown } from '../DropDown/DropDown'
import { ModalActionTableInfo } from '../Modal/ModalActionTableInfo'
import ViewItemModal from '../Modal/ViewItemModal'
import { Search } from '../Search/Search'
import { GridActions } from './GridActions'
import { TableToolbar } from './components/TableToolbar'
import { useTableData } from './hooks/useTableData'
import {
  COLORS,
  actionIconBaseSx,
  dataGridCssHoverSx,
  toggleButtonHoverSx,
  toggleGroupSelectedSx,
} from './styles'
import { ModalData, TableProps } from './types'
import { escapeRegExp } from './utils'

export const Table = ({
  columns: customColumns,
  getData,
  handleDelete,
  titleKey,
  goTo,
  defaultPageSize,
  model,
  additionalButtons,
  DetailComponent,
  size,
  ml,
  mr,
  permissionUpdate,
  permissionDelete,
  permissionView,
  canShowRecycleButtons,
  permissionViewRecycle,
  permissionUpdateRecycle,
  handleRecycle,
  canShowUnarchiveButtons,
  permissionViewUnarchive,
  permissionUpdateUnarchive,
  handleUnarchive,
  shouldHideAuditory = false,
  renderExtraRowActions,
  renderExtraToolbarActions,
  onBeforeEdit,
}: TableProps) => {
  const navigate = useNavigate()
  const { handleOpen, handleSetObjectId, openDrawer } = useContext(ActivityContext)
  const permissions = useMemo(() => JSON.parse(getPermissions()), [])
  const tableData = useTableData({ getData, defaultPageSize })
  const [selectionModel, setSelectionModel] = useState<any[]>([])
  const [checkbox, setCheckbox] = useState(false)
  const [modalData, setModalData] = useState<ModalData>({ type: null })
  const [viewId, setViewId] = useState<string | null>(null)
  const canShowEditButton =
    (tableData.activeFilter === 'getDeleted' && permissionUpdateRecycle) ||
    (tableData.activeFilter === 'getArchived' && permissionUpdateUnarchive) ||
    !tableData.activeFilter
  const canShowRecycleButton = Boolean(canShowRecycleButtons && permissionUpdateRecycle)
  const canShowUnarchiveButton = Boolean(canShowUnarchiveButtons && permissionUpdateUnarchive)
  const canDisplayFilterToggles = canShowUnarchiveButtons || canShowRecycleButtons

  const openDeleteModal = useCallback((id: string, name: string) => {
    setModalData({ type: 'delete', id, name })
  }, [])
  const openRecycleModal = useCallback((id: string, name: string) => {
    setModalData({ type: 'recycle', id, name })
  }, [])
  const openUnarchiveModal = useCallback((id: string, name: string) => {
    setModalData({ type: 'unarchive', id, name })
  }, [])
  const closeModal = useCallback(() => {
    setModalData({ type: null })
  }, [])

  const handleBulkDelete = useCallback(() => {
    const names = tableData.rows
      .filter((row) => selectionModel.some((id) => id === row.id))
      .map((row) => row[titleKey])
      .join(', ')
    setModalData({ type: 'bulkDelete', name: names })
  }, [tableData.rows, selectionModel, titleKey])

  const handleModalConfirm = async () => {
    try {
      switch (modalData.type) {
        case 'delete':
          await handleDelete?.(modalData.id!, modalData.name!)
          await tableData.fetchData({
            params: { page: tableData.page, size: tableData.rowsPerPage },
          })
          setViewId(null)
          break
        case 'recycle':
          await handleRecycle?.(modalData.id!, modalData.name!)
          await tableData.fetchData({
            params: { page: tableData.page, size: tableData.rowsPerPage },
          })
          setViewId(null)
          break
        case 'unarchive':
          await handleUnarchive?.(modalData.id!, modalData.name!)
          await tableData.fetchData({
            params: { page: tableData.page, size: tableData.rowsPerPage },
          })
          setViewId(null)
          break
        case 'bulkDelete': {
          const deletions = tableData.rows
            .filter((row) => selectionModel.some((id) => id === row.id))
            .map((row) => handleDelete?.(row.id, modalData.name!))
          await Promise.all(deletions)
          await tableData.fetchData({
            params: { page: 0, size: tableData.rowsPerPage },
          })
          setSelectionModel([])
          break
        }
      }
    } catch (error) {
      console.error('[Table] Error during modal action:', error)
    }
    closeModal()
  }

  const getModalTitle = (type: ModalData['type']): string => {
    switch (type) {
      case 'delete':
        return 'table.modal.delete.title'
      case 'bulkDelete':
        return selectionModel.length === 1
          ? 'table.modal.delete.title'
          : 'table.modal.delete.all.title'
      case 'recycle':
        return 'table.modal.recycle.title'
      case 'unarchive':
        return 'table.modal.unarchive.title'
      default:
        return ''
    }
  }

  const onRowsSelectionHandler = useCallback((ids: any[]) => {
    if (!tableData.loading) {
      setCheckbox(Boolean(ids.length))
      setSelectionModel(ids)
    }
  }, [tableData.loading])

  const handleActivity = useCallback((id: string) => {
    handleSetObjectId(id)
    handleOpen()
  }, [handleSetObjectId, handleOpen])

  const viewRow = tableData.rows.find((row) => row.id === viewId)
  const viewName = viewRow ? viewRow[titleKey] : ''

  const searchRegex = useMemo(() => {
    if (!tableData.termSearch) return null
    try {
      const escapedTerm = escapeRegExp(tableData.termSearch)
      return new RegExp(escapedTerm, 'gi')
    } catch {
      return null
    }
  }, [tableData.termSearch])

  const getCellClassName = useCallback(
    (params: GridCellParams<any, any, number>) => {
      if (params.field === 'id' || params.value == null) return ''
      if (!searchRegex) return ''
      return params.value.toString().match(searchRegex) !== null ? 'emphasis' : ''
    },
    [searchRegex]
  )

  const actionsColumn = useMemo(
    () => ({
      field: 'actions',
      type: 'actions' as const,
      width: renderExtraRowActions ? 290 : 265,
      headerName: '',
      cellClassName: 'acoes',
      getActions: ({ row, id }: { row: any; id: any }) => {
        const actions = [
          <GridActions
            key="view"
            sx={actionIconBaseSx}
            icon={
              <Tooltip title={t('form.button.to_view')}>
                <PreviewIcon />
              </Tooltip>
            }
            label="View"
            className="textPrimary"
            onClick={() => setViewId(row.id)}
            disabled={permissionView}
            color="inherit"
          />,
          <GridActions
            key="edit"
            sx={{
              display: canShowEditButton ? '' : 'none',
              ...actionIconBaseSx,
            }}
            icon={
              <Tooltip title={t('table.button.edit')}>
                <EditIcon />
              </Tooltip>
            }
            label="Edit"
            className="textPrimary"
            onClick={() => goTo?.(navigate, row.id)}
            disabled={permissionUpdate}
            color="inherit"
          />,
          <GridActions
            key="delete"
            sx={actionIconBaseSx}
            icon={
              <Tooltip title={t('table.button.delete')}>
                <DeleteIcon />
              </Tooltip>
            }
            onClick={() => openDeleteModal(row.id, row[titleKey])}
            disabled={permissionDelete}
            label="Delete"
            color="inherit"
          />,
          <GridActions
            key="recycle"
            sx={{
              display: canShowRecycleButton ? '' : 'none',
              ...actionIconBaseSx,
            }}
            icon={
              <Tooltip title={t('table.button.recycle')}>
                <RecyclingIcon />
              </Tooltip>
            }
            onClick={() => openRecycleModal(row.id, row[titleKey])}
            disabled={!permissionUpdateRecycle}
            label="Recycle"
            color="inherit"
          />,
          <GridActions
            key="unarchive"
            sx={{
              display: canShowUnarchiveButton ? '' : 'none',
              ...actionIconBaseSx,
            }}
            icon={
              <Tooltip title={t('table.button.unarchive')}>
                <UnarchiveIcon />
              </Tooltip>
            }
            onClick={() => openUnarchiveModal(row.id, row[titleKey])}
            disabled={!permissionUpdateUnarchive}
            label="Unarchive"
            color="inherit"
          />,
          <GridActions
            key="activity"
            sx={actionIconBaseSx}
            icon={
              <Tooltip title={t('table.button.activity')}>
                <RestoreIcon />
              </Tooltip>
            }
            label={t('table.button.activity')}
            color="inherit"
            onClick={() => handleActivity(row.id)}
            disabled={
              shouldHideAuditory ||
              !permissions?.auditory?.includes('auditory:list')
            }
          />,
        ]
        if (renderExtraRowActions) {
          const extras = renderExtraRowActions(row, true, tableData.refreshData)
          if (extras) actions.push(...(extras as JSX.Element[]))
        }

        return actions
      },
    }),
    [
      renderExtraRowActions,
      permissionView,
      permissionUpdate,
      permissionDelete,
      canShowEditButton,
      canShowRecycleButton,
      canShowUnarchiveButton,
      permissionUpdateRecycle,
      permissionUpdateUnarchive,
      shouldHideAuditory,
      permissions,
      titleKey,
      navigate,
      goTo,
      openDeleteModal,
      openRecycleModal,
      openUnarchiveModal,
      handleActivity,
      tableData.refreshData,
    ]
  )

  const columns = useMemo(
    () => [...customColumns, actionsColumn],
    [customColumns, actionsColumn]
  )

  const dataGridSx = useMemo(
    () => ({
      '.MuiDataGrid-columnSeparator': {
        display: 'none',
      },
      '.MuiTablePagination-root': {
        color: 'rgba(0, 0, 0, 0.6)',
        '.MuiSelect-select': {
          color: '#000',
        },
      },
      '& .emphasis': {
        fontWeight: 600,
        color: COLORS.primary,
      },
      ...dataGridCssHoverSx,
    }),
    []
  )

  const localeText = useMemo(
    () => ({
      noResultsOverlayLabel: t('table.no.results'),
      MuiTablePagination: {
        labelRowsPerPage: `${t('table_label_rows_per_page')}`,
        labelDisplayedRows: () => {
          return (
            <Box
              component={'span'}
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: '5px',
              }}
            >
              {t('table_page')}
              <Box
                component={'span'}
                sx={{ color: '#000', fontSize: '14px' }}
              >
                {tableData.page + 1}
              </Box>
              {t('table_in')}
              <Box
                component={'span'}
                sx={{ color: '#000', fontSize: '14px' }}
              >
                {tableData.totalPages > 0 ? tableData.totalPages : 1}
              </Box>
            </Box>
          )
        },
      },
      footerRowSelected: (n: number) => `${n} ${t('table.selected.rows')}`,
    }),
    [tableData.page, tableData.totalPages]
  )

  const handleViewId = useCallback((id: string) => setViewId(id), [])
  const handleCloseView = useCallback(() => setViewId(null), [])
  const handlePageSizeChange = useCallback(
    (value: number) => tableData.setRowsPerPage(value),
    [tableData.setRowsPerPage]
  )
  const handlePageChange = useCallback(
    (newPage: number) => tableData.setPage(newPage),
    [tableData.setPage]
  )

  const componentsProps = useMemo(
    () => ({
      toolbar: {
        selectionModel,
        checkbox,
        rows: tableData.rows,
        titleKey,
        model,
        permissionView,
        permissionUpdate,
        permissionDelete,
        canShowEditButton,
        canShowRecycleButton,
        canShowUnarchiveButton,
        permissionUpdateRecycle,
        permissionUpdateUnarchive,
        goTo,
        onView: handleViewId,
        onDelete: openDeleteModal,
        onBulkDelete: handleBulkDelete,
        onRecycle: openRecycleModal,
        onUnarchive: openUnarchiveModal,
        onActivity: handleActivity,
        onBeforeEdit,
        renderExtraToolbarActions,
        refreshData: tableData.refreshData,
      } as any,
      pagination: {
        showFirstButton: true,
        showLastButton: true,
      },
    }),
    [
      selectionModel,
      checkbox,
      tableData.rows,
      tableData.refreshData,
      titleKey,
      model,
      permissionView,
      permissionUpdate,
      permissionDelete,
      canShowEditButton,
      canShowRecycleButton,
      canShowUnarchiveButton,
      permissionUpdateRecycle,
      permissionUpdateUnarchive,
      goTo,
      handleViewId,
      openDeleteModal,
      handleBulkDelete,
      openRecycleModal,
      openUnarchiveModal,
      handleActivity,
      onBeforeEdit,
      renderExtraToolbarActions,
    ]
  )

  return (
    <Box
      component={'div'}
      sx={{ width: openDrawer ? 'calc(100% - 280px)' : '100%' }}
    >
      {modalData.type && (
        <ModalActionTableInfo
          open={true}
          name={modalData.name || ''}
          title={getModalTitle(modalData.type)}
          onHandleClose={closeModal}
          onHandleDeleteClick={handleModalConfirm}
        />
      )}
      {viewId && DetailComponent && (
        <ViewItemModal
          goTo={goTo}
          id={viewId}
          name={viewName}
          open={true}
          handleClose={handleCloseView}
          size={size}
          mr={mr}
          ml={ml}
          handleOpenModal={openDeleteModal}
          showEditButton={permissionUpdate}
        >
          <DetailComponent isViewMode={true} viewId={viewId} />
        </ViewItemModal>
      )}
      <Box
        component={'div'}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          mt: 1,
        }}
      >
        <Box
          sx={{
            width: '100%',
            justifyContent: 'space-between',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Box sx={{ width: '50%' }}>
            <Search onSearch={tableData.handleSearch} />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {canDisplayFilterToggles && (
              <ToggleButtonGroup
                value={tableData.activeFilter}
                onChange={tableData.handleSelectFilter}
                aria-label="select special condition"
                exclusive
                sx={toggleGroupSelectedSx}
              >
                {permissionViewUnarchive && (
                  <ToggleButton
                    value="getArchived"
                    aria-label="get archived"
                    sx={toggleButtonHoverSx}
                  >
                    <UnarchiveIcon />
                  </ToggleButton>
                )}
                {permissionViewRecycle && (
                  <ToggleButton
                    value="getDeleted"
                    aria-label="get deleted"
                    sx={{
                      ...toggleButtonHoverSx,
                      ':hover': {
                        ...toggleButtonHoverSx[':hover'],
                        borderLeft: undefined,
                      },
                    }}
                  >
                    <RecyclingIcon />
                  </ToggleButton>
                )}
              </ToggleButtonGroup>
            )}
            {additionalButtons}
            <Dropdown
              icon={<FilterListIcon />}
              title={t('table.order.orderBy')}
              itens={[
                {
                  title: (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        ml: tableData.orderBy !== 'desc' && '38px',
                        fontWeight: tableData.orderBy !== 'asc' && 700,
                      }}
                    >
                      {tableData.orderBy === 'desc' && (
                        <CheckIcon
                          sx={{ color: COLORS.primary, fontSize: '14px' }}
                        />
                      )}
                      {t('table.order.orderBy.desc')}
                    </Box>
                  ),
                  onClick: () => tableData.setOrderBy('desc'),
                },
                {
                  title: (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        ml: tableData.orderBy !== 'asc' && '38px',
                        fontWeight: tableData.orderBy !== 'desc' && 700,
                      }}
                    >
                      {tableData.orderBy === 'asc' && (
                        <CheckIcon
                          sx={{ color: COLORS.primary, fontSize: '14px' }}
                        />
                      )}
                      {t('table.order.orderBy.asc')}
                    </Box>
                  ),
                  onClick: () => tableData.setOrderBy('asc'),
                },
              ]}
            />
          </Box>
        </Box>
      </Box>
      <Box
        component={'div'}
        sx={{
          height: tableData.rowsPerPage === 5 ? 371 : 570,
          mb: 2,
          mt: '16px',
          width: '100%',
        }}
      >
        <DataGrid
          components={{
            Toolbar: TableToolbar,
          }}
          sx={dataGridSx}
          getCellClassName={getCellClassName}
          keepNonExistentRowsSelected
          onSelectionModelChange={(ids) => onRowsSelectionHandler(ids as any[])}
          selectionModel={selectionModel}
          checkboxSelection
          rows={tableData.rows}
          columns={columns}
          pageSize={tableData.rowsPerPage}
          rowsPerPageOptions={[5, 15, 30]}
          onPageSizeChange={handlePageSizeChange}
          pagination
          disableColumnMenu
          page={tableData.page}
          rowCount={tableData.totalPages * tableData.rowsPerPage || 1}
          paginationMode="server"
          componentsProps={componentsProps}
          onPageChange={handlePageChange}
          loading={tableData.loading}
          localeText={localeText}
        />
      </Box>
    </Box>
  )
}
